import { Dispatch } from '@reduxjs/toolkit';
import { filter, keyBy, uniqBy } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Account } from 'dashboard/models/account';
import { Case } from 'dashboard/models/case';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplate, CaseTemplateStatus } from 'dashboard/models/case-template';
import { Environment } from 'dashboard/models/environment';
import { ExporterCase, ExporterFileType } from 'dashboard/models/exporter';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import {
  CasesFilterParams,
  setAllCaseTemplates,
  setCases,
  setCasesDetails,
  setCasesRequestKey,
} from 'dashboard/redux/actions/cases';
import { ApiEndpoint, ApiModel, PaginationParams } from 'lib/interfaces/api';

export function loadCasesList({
  currentEnvironment,
  currentPage,
  pageSize,
  sortParams,
  filters,
  isPollRequest = false,
}: {
  currentEnvironment: Environment;
  currentPage: number;
  pageSize: number;
  sortParams: SortParams;
  filters: CasesFilterParams;
  isPollRequest?: boolean;
  abortSignal?: AbortSignal;
}) {
  return async (dispatch: Dispatch) => {
    const params = createParamsForList(currentEnvironment, filters, sortParams);
    const options = {
      ...params,
      page: {
        number: currentPage,
        size: pageSize,
      },
    } as any;

    const requestOptionsKey = JSON.stringify(options);
    dispatch(setCasesRequestKey(requestOptionsKey));

    if (isPollRequest) {
      options.headers = {
        'Persona-Skip-Log-User-Action': 'true',
      };
    }

    const casesResponse = await SessionClientDashboard.listResource<Case, PaginationParams>(
      ApiEndpoint.Case,
      options,
    );

    const includedItemsMap = keyBy('id', casesResponse.included);

    const allUsers = filter(
      (apiRelationship) => apiRelationship.type === ApiModel.User,
      includedItemsMap,
    ) as User[];
    const users = uniqBy('id', allUsers);

    const allCaseQueues = filter(
      (apiRelationship) => apiRelationship.type === ApiModel.CaseQueue,
      includedItemsMap,
    ) as CaseQueue[];
    const caseQueues = uniqBy('id', allCaseQueues);

    const allCaseTemplates = filter(
      (apiRelationship) => apiRelationship.type === ApiModel.CaseTemplate,
      includedItemsMap,
    ) as CaseTemplate[];
    const caseTemplates = uniqBy('id', allCaseTemplates);

    const allAccounts = filter(
      (apiRelationship) => apiRelationship.type === ApiModel.Account,
      includedItemsMap,
    ) as Account[];
    const accounts = uniqBy('id', allAccounts);

    const allTags = filter(
      (apiRelationship) => apiRelationship.type === ApiModel.Tag,
      includedItemsMap,
    ) as Tag[];
    const tags = uniqBy('id', allTags);

    const cases = casesResponse.data;
    const paginationParams = casesResponse.meta;

    dispatch(setCasesDetails(accounts, caseQueues, caseTemplates, users, tags));
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setCases(cases, requestOptionsKey, paginationParams));
  };
}

export function loadCaseTemplatesForFilter() {
  return async (dispatch: Dispatch) => {
    const params = {
      filter: {
        status: CaseTemplateStatus.Active,
      },
      page: {
        // use a large page size in case an organization has many case templates
        size: 100,
      },
    };

    const caseTemplatesResponse = await SessionClientDashboard.listResource<
      CaseTemplate,
      PaginationParams
    >(ApiEndpoint.CaseTemplate, params);
    const caseTemplates = caseTemplatesResponse.data;
    dispatch(setAllCaseTemplates(caseTemplates));
  };
}

export const loadExporterCase =
  ({
    currentEnvironment,
    filters,
    sortParams,
    fileType,
  }: {
    currentEnvironment: Environment;
    filters: CasesFilterParams;
    sortParams: SortParams;
    fileType: ExporterFileType;
  }) =>
  async () => {
    const params = createParamsForList(currentEnvironment, filters, sortParams, fileType);

    await SessionClientDashboard.postResourceCreate<ExporterCase>({
      type: ApiModel.ExporterCase,
      attributes: {
        ...params,
      },
    });
  };

export function createParamsForList(
  currentEnvironment: Environment,
  caseFilters: CasesFilterParams,
  caseSortParams: SortParams,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  fileType: ExporterFileType = null,
) {
  const {
    startDate,
    endDate,
    dateType,
    status,
    resolutions,
    caseTemplateIds,
    caseStateNames,
    caseQueueIds,
    tagsConditions,
    assigneeIds,
    notAssigneeIds,
    teamAssigneeIds,
    omni,
  } = caseFilters;
  const { sort, shouldSortDescending } = caseSortParams;

  return {
    'file-type': fileType,
    filter: {
      'environment-id': currentEnvironment.id,
      'start-date': startDate?.toString(),
      'end-date': endDate?.toString(),
      'date-type': dateType,
      status,
      resolution: resolutions,
      // convert an array of string joined by comma which stands for all state ids that have the same state name
      // to an array of individual token
      caseStateNames: caseStateNames === 'all' ? [] : caseStateNames,
      caseTemplateIds: caseTemplateIds === 'all' ? [] : caseTemplateIds,
      caseQueueIds: caseQueueIds === 'all' ? [] : caseQueueIds,
      tagsConditions,
      assigneeIds: assigneeIds === 'all' ? [] : assigneeIds,
      notAssigneeIds,
      teamAssigneeIds: teamAssigneeIds === 'all' ? [] : teamAssigneeIds,
      omni,
    },
    sort: `${shouldSortDescending ? '-' : ''}${sort}`,
  };
}
